import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { tokens } from '../../global/theme/tokens';
import { AiChatMessage } from './AiChatMessage';
import { AiChatApprovalCard } from './AiChatApprovalCard';
import { AiChatQuestionCard } from './AiChatQuestionCard';
import { referenceLabel } from './AiReferenceChip';

export const AiChatMessageList = ({
  messages,
  pendingMessage,
  // the answer as it is generated (useAiChatSession), shown after the transcript
  streamingMessage = null,
  status,
  isBusy,
  isLoading,
  pageContext,
  onRetry,
  // why the last turn failed ({ reason, message }), shown with the retry
  lastError = null,
  pendingApprovals = [],
  canDecideApprovals,
  onDecideApprovals,
  contentClassName,
  // replaces the default empty state (a purpose chat explains its job)
  emptyState = null,
}) => {
  const scrollRef = useRef(null);

  const visibleMessages = [
    ...messages,
    ...(pendingMessage ? [pendingMessage] : []),
    ...(streamingMessage ? [streamingMessage] : []),
  ];

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [
    visibleMessages.length,
    isBusy,
    status,
    pendingApprovals.length,
    streamingMessage?.content.length,
    streamingMessage?.toolCalls.length,
  ]);

  const isEmpty = visibleMessages.length === 0 && !isBusy;

  // a paused turn waits for permissions, for answers to questions, or both
  const questions = pendingApprovals.filter((a) => a.kind === 'questions');
  const permissions = pendingApprovals.filter((a) => a.kind !== 'questions');

  return (
    <Box ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
      <Box className={contentClassName}>
        {isLoading && visibleMessages.length === 0 ? (
          <Box className="flex justify-center py-10">
            <CircularProgress size={24} />
          </Box>
        ) : isEmpty ? (
          emptyState || <EmptyState pageContext={pageContext} />
        ) : (
          <Box className="flex flex-col gap-4">
            {visibleMessages.map((message) => (
              <AiChatMessage key={message.id} message={message} />
            ))}
            {status === 'awaiting_approval' && pendingApprovals.length > 0 ? (
              <>
                {questions.length > 0 && (
                  <AiChatQuestionCard
                    requests={questions}
                    canDecide={canDecideApprovals}
                    onDecide={onDecideApprovals}
                  />
                )}
                {permissions.length > 0 && (
                  <AiChatApprovalCard
                    approvals={permissions}
                    canDecide={canDecideApprovals}
                    onDecide={onDecideApprovals}
                  />
                )}
              </>
            ) : (
              // the streamed answer shows its own cursor once text arrives
              isBusy &&
              !streamingMessage?.content && <ThinkingIndicator status={status} />
            )}
            {status === 'failed' && !isBusy && (
              <FailedNotice error={lastError} onRetry={onRetry} />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const EmptyState = ({ pageContext }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box className="flex flex-col items-center text-center gap-2 pt-16 px-4">
      <Box
        className="flex items-center justify-center rounded-2xl mb-2"
        sx={{
          width: 56,
          height: 56,
          backgroundColor: `${colors.info}20`,
          color: colors.info,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 30 }} />
      </Box>
      <Typography variant="h4" fontWeight={600}>
        {t('How can I help?')}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.6, maxWidth: 300 }}>
        {pageContext
          ? t('The current page is attached as context: {{label}}', {
              label: referenceLabel(pageContext, t),
            })
          : t(
              'Ask a question or link projects, documents or contacts as context.',
            )}
      </Typography>
    </Box>
  );
};

const STATUS_TEXT = {
  running: 'Thinking...',
  awaiting_approval: 'Waiting for your decision',
  awaiting_budget: 'Waiting for AI budget',
};

const ThinkingIndicator = ({ status }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box className="flex items-center gap-2">
      <Box className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: colors.info,
              animation: 'ai-thinking-dot 1.4s infinite ease-in-out',
              animationDelay: `${i * 0.2}s`,
              '@keyframes ai-thinking-dot': {
                '0%, 80%, 100%': { opacity: 0.25, transform: 'scale(0.8)' },
                '40%': { opacity: 1, transform: 'scale(1)' },
              },
            }}
          />
        ))}
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        {t(STATUS_TEXT[status] || STATUS_TEXT.running)}
      </Typography>
    </Box>
  );
};

// texts per failure reason (backend AiTurnError), the rest is "interrupted"
const FAILURE_TEXT = {
  budget_exhausted:
    'The AI token budget is used up, so no answer could be generated.',
  budget_unavailable:
    'No answer could be generated because no AI token budget is available.',
  timeout: 'The answer took too long and was cancelled.',
};

const FailedNotice = ({ error, onRetry }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box
      className="flex items-center gap-2 px-3 py-2 rounded-lg"
      sx={{
        backgroundColor: `${colors.error}15`,
        border: `1px solid ${colors.error}40`,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 18, color: colors.error }} />
      <Box className="flex-1 min-w-0">
        <Typography
          variant="caption"
          sx={{ display: 'block', color: colors.text }}
        >
          {t(FAILURE_TEXT[error?.reason] || 'The last answer was interrupted.')}
        </Typography>
        {error?.message && (
          <Typography
            variant="caption"
            sx={{ display: 'block', opacity: 0.6, wordBreak: 'break-word' }}
          >
            {error.message}
          </Typography>
        )}
      </Box>
      {onRetry && (
        <Button
          size="small"
          color="secondary"
          startIcon={<ReplayIcon />}
          onClick={onRetry}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          {t('Try again')}
        </Button>
      )}
    </Box>
  );
};
